import { type Row, number, ratio, sum, text } from './liveModel';

export const PAGE_ID = 'commercial-kpi-tracker';
export const METRICS = [
  ['net_margin_adjusted', 'Net margin (excl. experiences)'],
  ['calls', 'Calls / VC'],
  ['meetings', 'Meetings'],
  ['proposals', 'Proposals'],
  ['net_signings', 'Net signings'],
] as const;
export const COMMON_FILTERS = [
  ['pcm_name', 'PCM / MM'],
  ['rsm', 'Regional manager'],
  ['pcm_region', 'PCM region'],
  ['rsm_region', 'RSM region'],
  ['category', 'Category'],
] as const;
export const OFFER_FILTERS = [
  ['brand_name', 'Brand'],
  ['product_name', 'Product'],
  ['category_name', 'Product category'],
  ['vendor_name', 'Vendor'],
  ['offer_country_name', 'Destination country'],
  ['offer_bk', 'Offer ID'],
  ['offer_name', 'Offer name'],
  ['offer_opportunity_name', 'Opportunity'],
  ['offer_start_date', 'Offer start date'],
  ['offer_end_date', 'Offer end date'],
  ['offer_continent', 'Continent'],
  ['offer_state_name_au', 'Australian state'],
  ['offer_geo_area_level_1', 'Destination region'],
  ['offer_geo_area_level_2', 'Destination city'],
  ['deal_tier', 'Deal tier'],
  ['salesperson_gross_sales_forecast', 'Sales forecast'],
  ['salesperson_margin_forecast', 'Margin forecast'],
  ['vendor_payment_method', 'Vendor payment method'],
  ['pcm_margin_deduction', 'PCM margin deduction'],
  ['opportunity_stage_name', 'Opportunity stage'],
  ['opportunity_close_date', 'Opportunity close date'],
  ['contract_signed_date', 'Contract signed date'],
] as const;
export const FILTER_KEYS = [...COMMON_FILTERS, ...OFFER_FILTERS].map(([k]) => k);
export function currentQuarter(now = new Date()) {
  const syd = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const year = syd.find((v) => v.type === 'year').value,
    month = +syd.find((v) => v.type === 'month').value;
  return `${year}-${String(Math.floor((month - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
}
export function quarterEnd(start: string) {
  const d = new Date(start + 'T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + 3);
  d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
}
export function targetStats(rows: Array<Row>, quarter: string, today: string) {
  const actual = sum(rows, 'actual_value'),
    target = sum(rows, 'target_value');
  const total = (Date.parse(quarterEnd(quarter)) - Date.parse(quarter)) / 86400000 + 1;
  const elapsed = Math.max(0, Math.min(total, (Date.parse(today) - Date.parse(quarter)) / 86400000));
  const forecast = elapsed > 0 && actual !== null ? (actual / elapsed) * total : null;
  return {
    actual,
    target,
    forecast,
    actual_ratio: target === 0 && rows.length ? 0 : ratio(actual, target),
    forecast_ratio: target === 0 && rows.length ? 0 : ratio(forecast, target),
    on_track:
      target === null || !rows.length
        ? 'Unknown'
        : target === 0
          ? 'Yes'
          : forecast === null
            ? 'Unknown'
            : forecast >= target
              ? 'Yes'
              : 'No',
    override: sum(rows, 'override_value'),
  };
}
export function targetRows(rows: Array<Row>, view: string, quarter: string, today: string): Array<Row> {
  const groups = new Map<string, Array<Row>>();
  rows
    .filter((r) => METRICS.some(([id]) => id === r.metric_name))
    .forEach((r) => {
      let tuple: Record<string, unknown>;
      try {
        tuple = JSON.parse(text(r.owner_tuple));
      } catch {
        tuple = {};
      }
      const key = JSON.stringify([
        tuple.pcm_name,
        tuple.pcm_region,
        view === 'MM' ? null : tuple.rsm_region,
        r.metric_name,
      ]);
      groups.set(key, [...(groups.get(key) ?? []), r]);
    });
  return [...groups.entries()]
    .map<Row>(([key, rs]) => {
      let tuple: Record<string, unknown>;
      try {
        tuple = JSON.parse(text(rs[0].owner_tuple));
      } catch {
        tuple = {};
      }
      return {
        key,
        ...tuple,
        metric_name: rs[0].metric_name,
        metric: METRICS.find(([id]) => id === rs[0].metric_name)?.[1] ?? rs[0].metric_name,
        owner_tuple: rs[0].owner_tuple,
        owner_tuples: rs.map((r) => r.owner_tuple),
        ...targetStats(rs, quarter, today),
      };
    })
    .sort(
      (a, b) =>
        text(a.pcm_name).localeCompare(text(b.pcm_name)) || text(a.metric_name).localeCompare(text(b.metric_name)),
    );
}
export function offerRows(rows: Array<Row>, basis: string): Array<Row> {
  return rows.map((r) => {
    let dims: Row;
    try {
      dims = JSON.parse(text(r.offer_mark));
    } catch {
      dims = {};
    }
    const br = basis === 'BR';
    return {
      ...dims,
      ...r,
      ttv: number(r[br ? 'ttv_br' : 'ttv_ar']),
      adjusted_ttv: number(r[br ? 'ttv_adjusted_br' : 'ttv_adjusted_ar']),
      gross_margin: number(r[br ? 'gm_br' : 'gm_ar']),
      adjusted_margin: number(r[br ? 'gm_adjusted_br' : 'gm_adjusted_ar']),
      display_name: ['Tourbank', 'Cruisebank'].includes(text(dims.product_name))
        ? dims.offer_name
        : dims.offer_opportunity_name,
      margin_forecast:
        number(dims.salesperson_margin_forecast) === null
          ? null
          : Math.trunc(number(dims.salesperson_margin_forecast)!),
      status:
        !dims.offer_start_date || !dims.offer_end_date
          ? 'Unknown'
          : new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }) < text(dims.offer_start_date)
            ? 'Not live'
            : new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }) >= text(dims.offer_end_date)
              ? 'Ended'
              : 'Live',
      refunds:
        number(r.ttv_adjusted_br) === null || number(r.ttv_adjusted_ar) === null
          ? null
          : number(r.ttv_adjusted_br) - number(r.ttv_adjusted_ar),
    };
  });
}

/** Exact original visible offer shelf grain; differing physical identities remain in evidence. */
export function groupOffers(rows: Row[], view: string): Row[] {
  const fields = [
    'vendor_payment_admin_fee',
    'ttv_br',
    'ttv_adjusted_br',
    'ttv_ar',
    'ttv_adjusted_ar',
    'gm_br',
    'gm_adjusted_br',
    'gm_ar',
    'gm_adjusted_ar',
    'promo_used_aud',
    'lp_margin_loss_sale',
    'lp_margin_gain_sale',
    'lp_margin_loss_refund',
    'lp_margin_gain_refund',
    'lp_margin_add_back_ar',
    'ics_cost_margin_reduction_aud',
    'pcm_tour_addback',
    'ten_percent_pcm_tour_margin_reduction',
    'number_of_vcc_deals',
    'number_of_deals_with_admin_fee',
    'number_of_deals_with_no_vcc_and_admin_fee',
    'total_number_of_applicable_deals',
    'net_margin_adjusted',
    'vendor_payment_method_margin_bonus',
    'vendor_payment_method_margin_penalty',
    'ttv',
    'gross_margin',
    'adjusted_ttv',
    'adjusted_margin',
    'refunds',
  ];
  const groups = new Map<string, Row[]>();
  const labels: Record<string, string> = {
    'Lux Partner Property': 'LPP',
    'Limited Time Lux Partner': 'LTS',
    'Limited Time Lux Exclusive': 'Flash',
    'Lux Premium Collection': 'LPC',
    'Last Minute Hotel': 'Last Minute',
  };
  rows.forEach((r) => {
    const product = labels[text(r.product_name)] ?? r.product_name;
    const key = JSON.stringify(
      view === 'MM'
        ? [r.pcm_name, r.pcm_region, r.offer_country_name, r.display_name, product]
        : [r.pcm_name, r.offer_country_name, r.display_name, r.status, product, r.margin_forecast],
    );
    groups.set(key, [...(groups.get(key) ?? []), r]);
  });
  return [...groups.entries()].map(([key, rs]) => {
    const first = rs[0],
      conflicts = Object.keys(first).filter(
        (k) => !fields.includes(k) && new Set(rs.map((r) => JSON.stringify(r[k]))).size > 1,
      );
    return {
      ...first,
      ...Object.fromEntries(conflicts.map((k) => [k, null])),
      ...Object.fromEntries(fields.map((k) => [k, sum(rs, k)])),
      key,
      source_rows: rs.length,
      source_evidence: rs,
      identity_conflicts: conflicts,
    };
  });
}
